const featureElementInstances = new WeakMap();
const productCardElementInstances = new WeakMap();
const selectorElementInstances = new WeakMap();
const specElementInstances = new WeakMap();

const DEFAULT_DESCRIPTION = "<p>My custom description</p>"

/**
 * @desc Converts km value to mi value
 * @param {number} kmValue 
 * @return {number}
 */
function convertKmToMi(kmValue) {
  const miValue = kmValue * 0.621371;
  if (Math.floor(miValue * 100) % 100 > 85) {
    return Math.ceil(miValue).toFixed(0);
  } else if (Math.floor(miValue * 100) % 100 > 5) {
    return Math.floor(miValue).toFixed(0) + '.5';
  }

  return miValue % 1 === 0 ? miValue.toFixed(0) : miValue.toFixed(1);
}

/**
 * @desc 
 *  Retrieves a common name for a group of products from the product 
 *  titles.
 * @param {{ title: string }[]} products 
 * @return {string}
 */
function findCommonName(products) {
  if (!products || products.length === 0) {
    return "";
  }

  let commonPrefix = products[0].title;

  for (let i = 1; i < products.length; i++) {
    while (products[i].title.indexOf(commonPrefix) !== 0) {
      commonPrefix = commonPrefix.slice(0, -1);
      if (commonPrefix === "") {
        return ""
      }
    }
  }

  const MIN_LENGTH = 3;
  if (commonPrefix.length < MIN_LENGTH || commonPrefix.match(/^-_$/)) {
    return "";
  }

  return commonPrefix;
}

function findProductFromVariantId(ranges, variantId) {
  const product = ranges
    .flatMap((range) => range.products)
    .find((product) => product.variants
      .find((variant) => variant.id === variantId));

  return product || null;
}

function findRangeFromVariantId(ranges, variantId) {
  if (!ranges || ranges.length <= 0 || !isValidNumber(variantId)) {
    throw new Error("Invalid argument passed to findRangeFromVariantId");
  }

  const matchingRange = ranges
    .find((range) => range.products
      .find((product) => product.variants
        .find((variant) => variant.id === variantId)));

  return matchingRange;
}

function findVariantFromRanges(ranges, variantId, selectorIndex = null) {
  const variantsData = ranges
    .flatMap((range) => range.products)
    .flatMap((product) => product.variants);

  if (
    selectorIndex !== null
    && isValidNumber(selectorIndex)
    && selectorIndex > 0
  ) {
    const productsData = ranges.flatMap((range) => range.products);
    const defaultProductIndex = productsData
      .findIndex((product) => product.variants
        .find((variant) => variant.id === variantId));

    if (defaultProductIndex) {
      let nextProductIndex = (Math.abs(selectorIndex) + defaultProductIndex) % productsData.length;
      if (nextProductIndex === defaultProductIndex) {
        nextProductIndex = defaultProductIndex + 1;
      }

      const nextProduct = productsData[nextProductIndex];
      const firstAvailableVariant = nextProduct.variants
        .find((variant) => variant.available);

      return firstAvailableVariant || nextProduct.variants[0];
    }

    const nextProduct = productsData[selectorIndex];
    const firstAvailableVariant = nextProduct.variants
      .find((variant) => variant.available);

    return firstAvailableVariant || nextProduct.variants[0];
  }

  const variant = variantsData
    .find((variant) => variant.id === variantId);

  return variant || null;
}

function formatNumberToPrice(numberToFormat) {
  // TODO: Update inline with current store locale
  return new Intl.NumberFormat(
    'en-GB',
    { style: 'currency', currency: 'GBP' },
  ).format(Number((numberToFormat / 100.00)));
}

/**
 * @desc 
 *  Formats the ranges data to a usable JSON format, including `name` 
 *  property.
 * @param {{ id: string, products: Object[] }[]} rangesData 
 * @return {{ id: string, name: string, products: Object[] }[]}
 * @throws {Error} When the provided ranges data is in an invalid format.
 */
function formatRangesData(rangesData) {
  if (!rangesData || !(rangesData instanceof Object)) {
    throw new Error("Ranges data is not in a valid format");
  }

  return rangesData.reduce((prev, curr) => {
    const commonName = findCommonName(curr.products);
    prev.push({
      id: curr.id,
      products: curr.products,
      name: commonName || curr.products[0]?.title || 'Unnamed',
    });

    return prev;
  }, [])
}

/**
 * @desc Determines if the provided value is a valid numeric type.
 * @param {any} value 
 * @return {boolean}
 */
function isValidNumber(value) {
  return Number.isFinite(parseInt(value)) && !isNaN(value);
}

/**
 * @desc Parses the data table contents to a readable JSON string.
 * @param {HTMLElement} dataTableElement 
 * @return {any}
 */
function parseLookupTableElement(dataTableElement) {
  if (!dataTableElement || !(dataTableElement instanceof HTMLElement)) {
    throw new Error("Failed to retrieve the lookup table element");
  }

  return JSON.parse(dataTableElement.textContent);
}

/**
 * @desc Retrieves the default product from the query params.
 * @return {string | null}
 */
function retrieveQueryParam() {
  const queryParams = new URLSearchParams(window.location.search);
  const defaultProduct = queryParams.get('variant');
  return defaultProduct;
}

function retrieveRangeStructure(optionId, rangesData) {
  for (const range of rangesData) {
    for (const product of range.products) {
      for (const variant of product.variants) {
        const option = variant.options.find((o) => o.id === Number(optionId));
        if (option) {
          return {
            ...product,
            variants: [
              {
                ...variant,
                options: [option],
              },
            ],
          };
        }
      }
    }
  }

  return null;
}

class AbstractComponent {
  constructor(baseElement, rangesData, defaultVariantId = null) {
    if (!baseElement || !(baseElement instanceof HTMLElement)) {
      throw new Error("Provided element is not a valid base element");
    }

    if (!rangesData) {
      throw new Error("Invalid argument supplied to rangesData");
    }

    // Elements
    this._comparisonContainerElement = document.querySelector(
      '[js-comparison="container"]',
    );
    this.baseElement = baseElement;

    // Binding functions
    this._handleColumnInitialised = this._handleColumnInitialised.bind(this);
    this._handleRangeUpdate = this._handleRangeUpdate.bind(this);
    this._handleOptionUpdate = this._handleOptionUpdate.bind(this);

    // Properties
    this._rangesData = rangesData;
    this._defaultVariantId = defaultVariantId;
  }

  init() {
    this._initListeners();
  }

  _initListeners() {
    this._comparisonContainerElement.addEventListener(
      "selector:change",
      this._handleRangeUpdate,
    );

    this._comparisonContainerElement.addEventListener(
      "option:change",
      this._handleOptionUpdate,
    );

    this._comparisonContainerElement.addEventListener(
      "column:initialise",
      this._handleColumnInitialised,
    );
  }

  _handleColumnInitialised(event) {
    const targetColumnId = event.detail
    if (!targetColumnId
      || !this.baseElement?.dataset.id
      || targetColumnId !== Number(this.baseElement.dataset.id)
    ) {
      return;
    }

    this.baseElement.classList.remove("product-comparison__component-hidden");
  }

  _handleRangeUpdate(event) {
    if (event.detail.id !== Number(this.baseElement.dataset.id)) {
      return;
    }

    const targetRange = this._rangesData[event.detail.to];
    this._updateFromRange(targetRange);
  }

  _handleOptionUpdate({ detail: { id, product, range, variant } }) {
    if (id !== Number(this.baseElement.dataset.id)) {
      return;
    }

    this._updateFromOption(range, product, variant);
  }

  _updateFromRange(_range, _product, _variant) {
    console.log("Updating from range");
  }

  _updateFromOption(_range, _product, _variant) {
    console.log("Updating from option");
  }

  _getDatasetValue(element, fallbackValue) {
    return element.dataset.value || fallbackValue;
  }
}

class Header {
  constructor(headerContainerElement, rangesData, defaultVariantId = null) {
    if (
      !(headerContainerElement instanceof HTMLElement)
      || !headerContainerElement
    ) {
      throw new Error("Provided element is not a valid header container");
    }

    // Composite properties for Header
    this.comparisonContainerElement = document
      .querySelector('[js-comparison="container"]');
    this._headerContainerElement = headerContainerElement;
    this._rangesData = rangesData;
    this._defaultVariantId = defaultVariantId;
    this._defaultRange = this._defaultVariantId ? this._rangesData.find(
      (range) => range.products.find(
        (product) => product.handle === this._defaultVariantId)) : null;

    // Element dictionary
    this._elements = {
      description: this._headerContainerElement.querySelector(
        '[js-comparison-header="description"]'),
      productName: this._headerContainerElement.querySelector(
        '[js-comparison-header="productName"]'),
      title: this._headerContainerElement.querySelector(
        '[js-comparison-header="title"]'),
    }

    // Binding properties
    this.handleOptionUpdate = this.handleOptionUpdate.bind(this);

    // Other properties
    this._dynamicTitleTemplate = this._elements.title?.dataset.dynamicTitle
      || 'Compare <span js-comparison-header="productName">{product}</span> '
      + 'with other electric scooters';
    this._fallbackDescription =
      this._elements.description?.dataset.fallbackDescription
      || "Compare Pure Electric's scooters";
    this._productName = this._elements.productName?.innerHTML || '';
    this._staticTitle = this._elements.title?.dataset.fallbackTitle
      || "Compare Pure Electric's scooters";

    // Initialisation
    this._initDescription();
    this._initTitle();
    this._initListeners();
  }

  _initDescription() {
    if (this._defaultVariantId) {
      this.updateDescription(
        this._defaultRange?.products[0].description || null);
    }
  }

  _initTitle() {
    if (this._defaultVariantId) {
      this.updateProductName(this._defaultRange?.products[0].title || null);
    }
  }

  _initListeners() {
    this.comparisonContainerElement.addEventListener(
      "option:change",
      this.handleOptionUpdate,
    )
  }

  handleOptionUpdate(event) {
    if (Number(event.detail.id) !== 0) {
      return;
    }

    this.updateDescription(event.detail.product.description || null);
    this.updateProductName(event.detail.product.title || null);
  }

  updateDescription(description = null) {
    if (!this._defaultVariantId) {
      return;
    }

    if (!description || description.length < 5) {
      this._elements.description.innerHTML = this._fallbackDescription;
    } else {
      this._elements.description.innerHTML = description;
    }
  }

  updateProductName(productName) {
    if (!this._defaultVariantId) {
      return;
    }

    if (!productName) {
      this._elements.title.innerHTML = this._staticTitle;
      return;
    }

    productName = productName.toLowerCase().includes("pure x mclaren")
      ? "Pure x McLaren"
      : productName;

    this._setProductName(productName);
    if (!this._elements.productName) {
      const newTitle = this
        .getDynamicTitleTemplate()
        .replace("{product}", productName);

      this._elements.title.innerHTML = newTitle;
      return;
    }

    this._elements.productName.innerHTML = productName;
  }

  getDynamicTitleTemplate() {
    return this._dynamicTitleTemplate;
  }

  getFallbackDescription() {
    return this._fallbackDescription;
  }

  getProductName() {
    return this._productName;
  }

  _setProductName(value) {
    if (!value || value.length < 3) {
      throw new Error("Invalid value passed to product name");
    }

    this._productName = value;
  }

  getStaticTitle() {
    return this._staticTitle;
  }
}

class Loading {
  constructor(loadingElement) {
    if (!loadingElement || !(loadingElement instanceof HTMLElement)) {
      throw new Error("Provided element is not a valid loading element");
    }

    this.loadingElement = loadingElement;
  }

  hide() {
    this.loadingElement.style.display = 'none';
  }

  show() {
    this.loadingElement.style.display = 'block';
  }
}

class BestForComponent extends AbstractComponent {
  constructor(
    bestForContainerElement,
    rangesData,
    defaultVariantId = null,
  ) {
    super(bestForContainerElement, rangesData, defaultVariantId);

    // Element dictionary
    this._elements = {
      adults: this.baseElement
        .querySelector("[js-comparison-best='adults']"),
      comfortableRides: this.baseElement
        .querySelector("[js-comparison-best='comfortableRides']"),
      dailyUse: this.baseElement
        .querySelector("[js-comparison-best='dailyUse']"),
      easyCarrying: this.baseElement
        .querySelector("[js-comparison-best='easyCarrying']"),
      fast: this.baseElement
        .querySelector("[js-comparison-best='fast']"),
      longDistances: this.baseElement
        .querySelector("[js-comparison-best='longDistances']"),
      rainyWeather: this.baseElement
        .querySelector("[js-comparison-best='rainyWeather']"),
      smallSpaces: this.baseElement
        .querySelector("[js-comparison-best='smallSpaces']"),
      steepHills: this.baseElement
        .querySelector("[js-comparison-best='steepHills']"),
      quickTrips: this.baseElement
        .querySelector("[js-comparison-best='quickTrips']"),
    }

    // Properties
    this._adults = this._getDatasetValue(this._elements.adults, false) === true;
    this._comfortableRides = this
      ._getDatasetValue(this._elements.comfortableRides, false) === true;
    this._dailyUse = this
      ._getDatasetValue(this._elements.dailyUse, false) === true;
    this._easyCarrying = this
      ._getDatasetValue(this._elements.easyCarrying, false) === true;
    this._fast = this
      ._getDatasetValue(this._elements.fast, false) === true;
    this._longDistances = this
      ._getDatasetValue(this._elements.longDistances, false) === true;
    this._rainyWeather = this
      ._getDatasetValue(this._elements.rainyWeather, false) === true;
    this._smallSpaces = this
      ._getDatasetValue(this._elements.smallSpaces, false) === true;
    this._steepHills = this
      ._getDatasetValue(this._elements.steepHills, false) === true;
    this._quickTrips = this
      ._getDatasetValue(this._elements.quickTrips, false) === true;

    // Initialisation
    this.init();
  }

  _updateFromOption(_, product) {
    this._updateValuesFromProduct(product);
  }

  _updateValuesFromProduct(product) {
    this._setAdults(
      product.bestFor.find((item) => item.name === "Adults") || {});
    this._setComfortableRides(
      product.bestFor.find((item) => item.name === "Comfortable rides") || {});
    this._setDailyUse(
      product.bestFor.find((item) => item.name === "Daily use") || {});
    this._setEasyCarrying(
      product.bestFor.find((item) => item.name === "Easy carrying") || {});
    this._setFast(
      product.bestFor.find((item) => item.name === "Fast") || {});
    this._setLongDistances(
      product.bestFor.find((item) => item.name === "Long distances") || {});
    this._setRainyWeather(
      product.bestFor.find((item) => item.name === "Rainy weather") || {});
    this._setSmallSpaces(
      product.bestFor.find((item) => item.name === "Small spaces") || {});
    this._setSteepHills(
      product.bestFor.find((item) => item.name === "Steep hills") || {});
    this._setQuickTrips(
      product.bestFor.find((item) => item.name === "Quick trips") || {});
  }

  _updateElement(element, data) {
    if ((!element || !(element instanceof HTMLElement))
      || typeof data !== "object"
    ) {
      throw new Error("Invalid setting for updating element");
    }

    element.dataset.value = (!!data.name).toString();
    element.classList.toggle(
      "product-comparison__item--hidden",
      !(!!data.name),
    );

    const link = element.querySelector("a");
    if (link) {
      link.href = data.collectionUrl || "";
    }
  }

  _setAdults(value) {
    this._updateElement(this._elements.adults, value);
    this._adults = !!value.name;
  }

  _setComfortableRides(value) {
    this._updateElement(this._elements.comfortableRides, value);
    this._comfortableRides = !!value.name;
  }

  _setDailyUse(value) {
    this._updateElement(this._elements.dailyUse, value);
    this._dailyUse = !!value.name;
  }

  _setEasyCarrying(value) {
    this._updateElement(this._elements.easyCarrying, value);
    this._easyCarrying = !!value.name;
  }

  _setFast(value) {
    this._updateElement(this._elements.fast, value);
    this._fast = !!value.name;
  }

  _setLongDistances(value) {
    this._updateElement(this._elements.longDistances, value);
    this._longDistances = !!value.name;
  }

  _setRainyWeather(value) {
    this._updateElement(this._elements.rainyWeather, value);
    this._rainyWeather = !!value.name;
  }

  _setSmallSpaces(value) {
    this._updateElement(this._elements.smallSpaces, value);
    this._smallSpaces = !!value.name;
  }

  _setSteepHills(value) {
    this._updateElement(this._elements.steepHills, value);
    this._steepHills = !!value.name;
  }

  _setQuickTrips(value) {
    this._updateElement(this._elements.quickTrips, value);
    this._quickTrips = !!value.name;
  }
}

class CardComponent extends AbstractComponent {
  constructor(cardContainerElement, rangesData, defaultVariantId = null) {
    super(cardContainerElement, rangesData, defaultVariantId);

    // Element dictionary
    this._elements = {
      atcButton: this.baseElement
        .querySelector("[js-comparison-card='atcButton']"),
      image: this.baseElement.querySelector("[js-comparison-card='image'] img"),
      imageContainer: this.baseElement
        .querySelector("[js-comparison-card='image']"),
      financePrice: this.baseElement
        .querySelector("[js-comparison-card='finance']"),
      financePriceContainer: this.baseElement
        .querySelector("[js-comparison-card='financeContainer']"),
      financePriceMessage: this.baseElement
        .querySelector("[js-comparison-card='financeMessage']"),
      imageLink: this.baseElement
        .querySelector("[js-comparison-card='imageLink']"),
      price: this.baseElement.querySelector("[js-comparison-card='price']"),
      reviewCount: this.baseElement
        .querySelector("[js-comparison-card='reviewCount']"),
      reviewStars: this.baseElement
        .querySelector("[js-comparison-card='reviewStars']"),
      salePrice: this.baseElement.querySelector("[js-comparison-card='sale']"),
      selectedVariantTitle: this.baseElement
        .querySelector("[js-comparison-card='selectedVariantTitle']"),
      title: this.baseElement.querySelector("[js-comparison-card='title']"),
      variantButtonTemplate: this.baseElement
        .querySelector("[js-comparison-card='variantButtonTemplate']"),
      variantIdInput: this.baseElement
        .querySelector("[js-comparison-card='variantId']"),
      variantOptionsContainer: this.baseElement
        .querySelector("[js-comparison-card='variantOptions']"),
      variantSelector: this.baseElement
        .querySelector("[js-comparison-card='variantSelector']"),
      viewLink: this.baseElement
        .querySelector("[js-comparison-card='viewLink']"),
    }

    // Bindings
    this._handleVariantSelectorChange = this._handleVariantSelectorChange
      .bind(this);

    // Properties
    this._available = this._elements.atcButton?.dataset.available === "true"
      || false;
    this._image = this._elements.image?.src || "";
    this._link = this._elements.viewLink?.href || "";
    this._price = this._elements.price?.dataset.price || -1;
    this._product = null;
    this._range = null;
    this._reviewCount = 0;
    this._reviewRating = 0;
    this._title = this._elements.title.innerHTML || "";
    this._variant = null;
    this._variantId = Number(this._elements.variantIdInput?.value || -1);
    this._variantName = this._elements.selectedVariantTitle.innerHTML || "";

    // Initialisation
    this.init();
  }

  _initListeners() {
    super._initListeners();
    this._comparisonContainerElement.addEventListener(
      "variantSelector:change",
      this._handleVariantSelectorChange,
    );
  };

  _updateFromOption(range, product, variant) {
    this._range = range;
    this._product = product;
    this._variant = variant;
    this._updateVariantSelector(range, variant);
    this._updateValuesFromProduct(product, variant);
  }

  _updateValuesFromProduct(product, variant) {
    this._setAvailable(variant.available);
    this._setImage(variant.featuredImage);
    this._setLink(variant.url);
    this._setTitle(product.title);
    this._setPrice(variant.price, variant.compareAtPrice || null);
    this._setReviewCount(product.reviews.count || 0);
    this._setReviewRating(product.reviews.rating || 0);
    this._setVariantId(variant.id);
    this._setVariantName(variant.options[0].name);
  }

  _updateVariantSelector(range, variant) {
    const variants = range.products.flatMap((product) => product.variants);
    const variantButtons = this.baseElement
      .querySelectorAll("[js-comparison-card='variantButton']");
    variantButtons.forEach((v) => v.remove());

    const swatches = [];
    variants.forEach((_variant, index) => {
      _variant.options.forEach((option) => {
        swatches.push(option.swatch);
        const clonedEl = this._elements.variantButtonTemplate.cloneNode();
        clonedEl.setAttribute("js-comparison-card", "variantButton");
        clonedEl.dataset.variant = option.id.toString();
        clonedEl.classList.toggle(
          "product-comparison__card-variant-option--line",
          swatches.filter((s) => s === option.swatch).length > 1
        );

        if (_variant.id === variant.id) {
          this._elements.selectedVariantTitle.innerHTML
            = variant.options[0].name;
          clonedEl.classList.add(
            "product-comparison__card-variant-option--selected"
          );
        }

        const colourSpanEl = document.createElement("span");
        colourSpanEl.style.backgroundColor = option.swatch || "#ffffff";

        clonedEl.appendChild(colourSpanEl);
        clonedEl.addEventListener("click", this._handleChangeVariant);
        this._elements.variantOptionsContainer.appendChild(clonedEl);
      })
    })
  }

  _handleChangeVariant(event) {
    const selectedClass = "product-comparison__card-variant-option--selected"
    const cardElement = event.currentTarget.closest("[js-comparison='card']");
    if (!event.currentTarget.classList.contains(selectedClass)) {
      const optionId = Number(event.currentTarget.dataset.variant);
      const currentlySelected = event.currentTarget
        .closest("[js-comparison-card='variantOptions']")
        .querySelector(`.${selectedClass}`)

      currentlySelected.classList.remove(selectedClass)
      event.target.classList.add(selectedClass);
      event
        .currentTarget
        .closest("[js-comparison='container']")
        .dispatchEvent(
          new CustomEvent(
            "variantSelector:change",
            {
              detail: {
                id: Number(cardElement.dataset.id),
                optionId,
              },
            },
          ),
        );
    }
  }

  _handleVariantSelectorChange({ detail: { id, optionId } }) {
    if (id !== Number(this.baseElement.dataset.id)) {
      return;
    }

    const matchingVariant = this._rangesData
      .flatMap((range) => range.products
        .flatMap((product) => product.variants))
      .find((variant) => variant.options
        .find((option) => option.id === optionId));

    if (!matchingVariant) {
      throw new Error("Unable to find matching variant from optionId");
    }

    const range = findRangeFromVariantId(this._rangesData, matchingVariant.id);
    const product = findProductFromVariantId(
      this._rangesData,
      matchingVariant.id,
    );
    const variant = findVariantFromRanges(this._rangesData, matchingVariant.id);

    if (!range || !product || !variant) {
      throw new Error(
        "Unable to find range, product, or variant from variantId"
      );
    }

    this._comparisonContainerElement.dispatchEvent(
      new CustomEvent(
        "option:change",
        {
          detail: {
            id,
            range,
            product,
            variant
          }
        },
      ),
    );
  }

  _setAvailable(value) {
    if (![true, false].includes(value)) {
      throw new Error("Invalid value passed to available");
    }

    this._elements.atcButton.dataset.available = value.toString();
    this._elements.atcButton.innerHTML = value ? "Add to cart" : "Out of stock";
    if (value) {
      this._elements.atcButton.removeAttribute("disabled")
    } else {
      this._elements.atcButton.setAttribute("disabled", "disabled");
    }

    this._available = value;
  }

  _setImage(value) {
    value = value.toString().trim();
    if (!value) {
      throw new Error("Invalid value passed to image");
    }

    const loadingClass = "product-comparison__card-image--loading";
    const fileRegex = /files\/(.*?)(?=\?)/g;

    this._elements.imageContainer.classList.add(loadingClass);
    this._elements.image.onload = () => {
      this._elements.imageContainer.classList.remove(loadingClass);
    }
    this._elements.image.onerror = () => {
      this._elements.imageContainer.classList.remove(loadingClass);
    }

    let srcsetString = "";
    for (let i = 0; i < 4; i++) {
      switch (i) {
        case 0:
          srcsetString += (value + "&width=352 352w,")
          break;
        case 1:
          srcsetString += (value + "&width=832 832w,")
          break;
        case 2:
          srcsetString += (value + "&width=1200 1200w,")
          break;
        case 3:
          srcsetString += (value + "&width=1920 1920w,")
          break;
        case 4:
          srcsetString += (value + "&width=2006 2006w")
          break;
      }
    }

    this._elements.image.src = this._elements.image.src = value;
    this._elements.image.srcset = srcsetString;

    this._image = value.toString().trim();
  }

  _setLink(value) {
    if (!value || !value.includes("/") || value.length < 1) {
      throw new Error("Invalid value passed to link");
    }

    this._elements.viewLink.href = value;
    this._elements.imageLink.href = value;
    this._link = value;
  }

  _setPrice(value, salePrice = null) {
    if (!isValidNumber(value)) {
      throw new Error("Invalid value passed to price");
    }

    this._elements.price.dataset.price = value.toString();
    this._elements.price.innerHTML = formatNumberToPrice(value);

    this._price = Number(value);

    const financeHiddenClass = "product-comparison__card-finance--hidden";
    this._elements.financePrice.dataset.price = Number(
      (salePrice ?? value) / 12.0
    )
      .toFixed(2)
      .toString();
    this._elements.financePrice.innerHTML = formatNumberToPrice(
      Number((salePrice ?? value) / 12.0)
    )
      + "/mo*";

    if (value < 41900 || (salePrice > 0 && salePrice < 41900)) {
      this._elements.financePriceContainer.classList.add(financeHiddenClass);
      this._elements.financePriceMessage.classList.remove(financeHiddenClass)
    } else {
      this._elements.financePriceMessage.classList.add(financeHiddenClass)
      this._elements.financePriceContainer.classList.remove(financeHiddenClass);
    }

    this._elements.salePrice.dataset.price = isValidNumber(salePrice)
      ? salePrice.toString()
      : '';
    this._elements.salePrice.innerHTML = isValidNumber(salePrice)
      ? formatNumberToPrice(salePrice)
      : '';
  }

  _setReviewCount(value) {
    if (!isValidNumber(value)) {
      throw new Error("Invalid value passed to reviewCount");
    }

    this._elements.reviewCount.innerHTML = `(${value})`;
    this._reviewCount = value;
  }

  _setReviewRating(value) {
    if (!isValidNumber(value)) {
      throw new Error("Invalid value passed to reviewStars");
    }

    const starEls = this._elements.reviewStars.querySelectorAll("i");
    const fullStarClasses = ["las", "la-star"];
    const halfStarClasses = ["las", "la-star-half-alt"];
    const emptyStarClasses = ["lar", "la-star"];

    const fullStarsCount = Math.floor(value);
    const halfStarsCount = value - fullStarsCount;

    function handleClasses(el, classesToAdd) {
      el.classList
        .remove(...fullStarClasses, ...halfStarClasses, ...emptyStarClasses);
      el.classList.add(...classesToAdd);
    }

    for (let i = 0; i < 5; i++) {
      handleClasses(starEls[i], emptyStarClasses)
    }

    if (fullStarsCount > 0) {
      for (let i = 0; i < fullStarsCount; i++) {
        handleClasses(starEls[i], fullStarClasses);
      }
    }

    if (halfStarsCount > 0.5) {
      handleClasses(starEls[fullStarsCount], halfStarClasses);
    }

    this._reviewRating = Math.floor(value);
  }

  _setTitle(value) {
    value = value.toString().trim();
    if (!value) {
      throw new Error("Invalid value passed to title");
    }

    if (value.includes("Pure x McLaren")) {
      value = "Pure x McLaren";
    }

    this._elements.title.innerHTML = value;
    this._title = value;
  }

  _setVariantName(value) {
    value = value.toString().trim();
    if (!value) {
      throw new Error("Invalid value passed to variantName");
    }

    this._elements.selectedVariantTitle.innerHTML = value;
    this._variantName = value;
  }

  _setVariantId(value) {
    if (!isValidNumber(value)) {
      throw new Error("Invalid value passed to variantId");
    }

    this._elements.variantIdInput.value = value.toString();
    this._variantId = value;
  }
}

class FeatureComponent extends AbstractComponent {
  constructor(
    featureContainerElement,
    rangesData,
    defaultVariantId = null
  ) {
    super(featureContainerElement, rangesData, defaultVariantId);

    // Element dictionary
    this._elements = {
      activeSteeringStabilisation: this.baseElement
        .querySelector("[js-comparison-feature='activeSteeringStabilisation']"),
      folding: this.baseElement
        .querySelector("[js-comparison-feature='folding']"),
      heavyDutyTyres: this.baseElement
        .querySelector("[js-comparison-feature='heavyDutyTyres']"),
      indicators: this.baseElement
        .querySelector("[js-comparison-feature='indicators']"),
      rubberDeck: this.baseElement
        .querySelector("[js-comparison-feature='rubberDeck']"),
      tenInchTyres: this.baseElement
        .querySelector("[js-comparison-feature='tenInchTyres']"),
      ultraCompact: this.baseElement
        .querySelector("[js-comparison-feature='ultraCompact']"),
      waterproof: this.baseElement
        .querySelector("[js-comparison-feature='waterproof']"),
    }

    // Properties
    this._activeSteeringStabilisation = this._getDatasetValue(
      this._elements.activeSteeringStabilisation, false
    ) === true;
    this._folding = this._getDatasetValue(
      this._elements.folding,
      false,
    ) === true
    this._heavyDutyTyres = this._getDatasetValue(
      this._elements.heavyDutyTyres, false
    ) === true;
    this._indicators = this._getDatasetValue(
      this._elements.indicators, false
    ) === true;
    this._rubberDeck = this._getDatasetValue(
      this._elements.rubberDeck, false
    ) === true;
    this._tenInchTyres = this._getDatasetValue(
      this._elements.tenInchTyres, false
    ) === true;
    this._ultraCompact = this._getDatasetValue(
      this._elements.ultraCompact, false
    ) === true;
    this._waterproof = this._getDatasetValue(
      this._elements.waterproof, false
    ) === true;

    // Initialisation
    this.init();
  }

  _updateFromOption(_, product) {
    this._updateValuesFromProduct(product);
  }

  _updateValuesFromProduct(product) {
    this._setActiveSteeringStabilisation(
      product.activeSteeringStabilisation || false
    );
    this._setFolding(product.folding || false);
    this._setHeavyDutyTyres(product.heavyDutyTyres || false);
    this._setIndicators(product.indicators || false);
    this._setRubberDeck(product.rubberDeck || false);
    this._setTenInchTyres(product.tenInchTyres || false);
    this._setUltraCompact(product.ultraCompact || false);
    this._setWaterproof(product.waterproof || false);
  }

  _updateElement(element, value) {
    if ((!element || !(element instanceof HTMLElement))
      || typeof value !== "boolean"
    ) {
      throw new Error("Invalid setting for updating element");
    }

    element.dataset.value = value.toString();
    element.classList.toggle("product-comparison__item--hidden", !value);
  }

  _setActiveSteeringStabilisation(value) {
    this._updateElement(this._elements.activeSteeringStabilisation, value);
    this._activeSteeringStabilisation = value;
  }

  _setFolding(value) {
    this._updateElement(this._elements.folding, value);
    this._folding = value;
  }

  _setHeavyDutyTyres(value) {
    this._updateElement(this._elements.heavyDutyTyres, value);
    this._heavyDutyTyres = value;
  }

  _setIndicators(value) {
    this._updateElement(this._elements.indicators, value);
    this._indicators = value;
  }

  _setRubberDeck(value) {
    this._updateElement(this._elements.rubberDeck, value);
    this._rubberDeck = value;
  }

  _setTenInchTyres(value) {
    this._updateElement(this._elements.tenInchTyres, value);
    this._tenInchTyres = value;
  }

  _setUltraCompact(value) {
    this._updateElement(this._elements.ultraCompact, value);
    this._ultraCompact = value;
  }

  _setWaterproof(value) {
    this._updateElement(this._elements.waterproof, value);
    this._waterproof = value;
  }
}

class InfoComponent extends AbstractComponent {
  constructor(infoElement, rangesData, defaultVariantId = null) {
    super(infoElement, rangesData, defaultVariantId);

    // Element dictionary
    this._elements = {
      toggle: this.baseElement
        .querySelector("[js-comparison-tooltip='toggle']"),
      tooltip: this.baseElement
        .querySelector("[js-comparison-tooltip='tooltip']"),
    }

    // Bindings
    this._handleHover = this._handleHover.bind(this);
    this._handleLeaveHover = this._handleLeaveHover.bind(this);
    this._handleClick = this._handleClick.bind(this);
    this._handleBlur = this._handleBlur.bind(this);

    // Properties
    this._isHidden = true;
    this._isTooltipOpen = false;
    this._targetKey = this.baseElement?.dataset.targetKey || "";
    this._targetValue = this.baseElement?.dataset.targetValue || "";

    // Initialisation
    this.init();
  }

  _initListeners() {
    super._initListeners();
    this._elements.toggle.addEventListener("mouseover", this._handleHover);
    this._elements.toggle.addEventListener("mouseout", this._handleLeaveHover);
    this._elements.toggle.addEventListener("click", this._handleClick);
    this._elements.toggle.addEventListener("blur", this._handleBlur);
  }

  _handleHover() {
    if (window.innerWidth > 1025) {
      this._setIsTooltipOpen(true);
    }
  }

  _handleLeaveHover() {
    if (window.innerWidth > 1025) {
      this._setIsTooltipOpen(false);
    }
  }

  _handleBlur() {
    if (window.innerWidth < 1025) {
      this._setIsTooltipOpen(false);
    }
  }

  _handleClick() {
    if (window.innerWidth < 1025) {
      this._setIsTooltipOpen(!this._isTooltipOpen);
    }
  }

  _updateFromOption(_, product) {
    const hiddenClass = "product-comparison__info--hidden";
    if (
      typeof product[this._targetKey] !== "boolean" && (
        !product[this._targetKey]
        || product[this._targetKey] !== this._targetValue
      )
    ) {
      this.baseElement.classList.add(hiddenClass);
    } else {
      this.baseElement.classList.remove(hiddenClass);
    }
    this._isHidden = !product[this._targetKey] === this._targetValue;
  }

  _setIsTooltipOpen(value) {
    this._elements.tooltip.classList.toggle(
      "product-comparison__info-tooltip--hidden",
      !value,
    );

    this._isTooltipOpen = value;
  }
}

class SelectorComponent extends AbstractComponent {
  constructor(selectorElement, rangesData, defaultVariantId = null) {
    super(selectorElement, rangesData, defaultVariantId);

    // Element dictionary
    this._elements = {
      atcForm: this.baseElement.parentElement
        .querySelector("[js-comparison-selector='atcForm']"),
    };

    // Bindings
    this._handleChange = this._handleChange.bind(this);

    // Properties
    this._currentRange = null;

    // Initialisation
    this.init();
  }

  init() {
    super.init();
    this._initOptions();
    this._initDefaultOption();
  }

  _initListeners() {
    super._initListeners();
    this.baseElement.addEventListener(
      "change",
      this._handleChange,
    );
  }

  _initOptions() {
    const loadingOptionEl = this.baseElement.querySelector("option");
    this._rangesData
      .sort((rangeA, rangeB) => rangeA.name > rangeB.name
        ? 1
        : rangeA.name < rangeB.name
          ? -1
          : 0)
      .forEach((range) => {
        const optGroup = document.createElement("optgroup");
        optGroup.label = range.name;

        const variants = range.products.reduce((prev, curr) => {
          return [...prev, ...curr.variants];
        }, []);

        variants.forEach((variant) => {
          const option = document.createElement("option");
          option.value = variant.id;
          option.innerHTML = `${range.name} ${variant.title}`;
          optGroup.appendChild(option);
        });

        this.baseElement.appendChild(optGroup);
      });

    this.baseElement.removeChild(loadingOptionEl);
  }

  _initDefaultOption() {
    const allProducts = this._rangesData.reduce((prev, curr) => {
      return [...prev, ...curr.products];
    }, []);

    let currentVariant = findVariantFromRanges(
      this._rangesData,
      Number(this._defaultVariantId),
      Number(this.baseElement.dataset.id),
    );

    if (!currentVariant) {
      currentVariant = getNextVariant(
        Number(this.baseElement.dataset.id),
        allProducts,
      );
    }

    this.baseElement.value = currentVariant.id;
    this.baseElement.dispatchEvent(new CustomEvent(
      "change",
      {
        detail: {
          isDefault: true,
        },
      },
    ));

    function getNextVariant(selectorIndex, products) {
      if (products.length <= 0) {
        throw new Error("The products array cannot be empty");
      }

      const productIndex = Math.abs(selectorIndex) % products.length;
      return products[productIndex].variants[0];
    }
  }

  _handleChange(event) {
    const variantId = Number(event.target.value);
    const selectorId = Number(event.target.dataset.id);
    const currentRange = findRangeFromVariantId(this._rangesData, variantId);

    const currentProduct = currentRange.products
      .find((product) => product.variants
        .find((variant) => variant.id === variantId));

    const currentVariant = findVariantFromRanges(this._rangesData, variantId);

    if (!currentRange) {
      throw new Error(
        "Could not find range matching variantId "
        + Number(event.target.value),
      );
    }

    if (!currentProduct || !currentVariant) {
      throw new Error(
        "Could not find variant matching variantId",
        Number(event.target.value),
      );
    }

    this._dispatchChangeEvent(
      selectorId,
      currentRange,
      currentProduct,
      currentVariant,
      event?.detail?.isDefault || false,
    );

    this._updateAtcButton(currentVariant);
  }

  _dispatchChangeEvent(id, range, product, variant, isDefault = false) {
    if (
      !isValidNumber(id)
      || !range
      || !product
      || !variant
    ) {
      throw new Error("Invalid value passed to dispatchChangeEvent");
    }

    if (!isDefault) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'selectChangeVariant',
        selectCustomData: [
          {
            product: {
              handle: product.handle,
              id: product.id,
              title: product.title,
              variant: {
                available: variant.available,
                compareAtPrice: variant.compareAtPrice,
                id: variant.id,
                price: variant.price,
                title: variant.title,
              },
            },
          },
        ],
        selectName: this.baseElement.getAttribute("js-comparison")
          + '-'
          + this.baseElement.dataset.id,
        selectValue: this.baseElement.value,
      });
    }


    this._comparisonContainerElement.dispatchEvent(
      new CustomEvent(
        "option:change",
        {
          detail: {
            id,
            range,
            product,
            variant,
          },
        },
      ),
    );
  }

  _dispatchInitColumnEvent(id) {
    if (!isValidNumber(id)) {
      throw new Error("Invalid ID passed to dispatchInitColumnEvent");
    }

    this._comparisonContainerElement.dispatchEvent(
      new CustomEvent(
        "column:initialise",
        { detail: id },
      ),
    );
  }

  _updateAtcButton(variant) {
    const formButton = this._elements.atcForm.querySelector("button");
    const formInput = this._elements.atcForm.querySelector("input");
    if (!formButton || !formInput) {
      return;
    }

    formButton.dataset.available = variant.available ? "true" : "false";
    formButton.innerHTML = variant.available
      ? "Add to cart"
      : "Out of stock";

    if (variant.available) {
      formButton.removeAttribute("disabled");
    } else {
      formButton.setAttribute("disabled", "disabled");
    }

    formInput.value = variant.id;
  }

  _updateFromOption(_, product, variant) {
    const formButton = this._elements.atcForm.querySelector("button");
    const formInput = this._elements.atcForm.querySelector("input");
    this.baseElement.value = variant.id;
    formButton.dataset.available = variant.available ? "true" : "false";
    formButton.innerHTML = variant.available
      ? !!product.preOrder
        ? "Pre-order"
        : "Add to cart"
      : "Out of stock";

    if (variant.available) {
      formButton.removeAttribute("disabled")
    } else {
      formButton.setAttribute("disabled", "disabled");
    }

    formInput.value = variant.id;
  }

  _setCurrentOption(value) {
    if (!isValidNumber(value)) {
      throw new Error("Invalid option selected");
    }

    const currentOption = getRangeFromOption(value, this._rangesData);

    function getRangeFromOption(index, ranges) {
      if (ranges.length <= 0) {
        throw new Error("The ranges array cannot be empty");
      }

      const loopedIndex = Math.abs(index) % ranges.length;
    }
  }

  _setCurrentRange(value) {
    if (!isValidNumber(value)) {
      this.baseElement.value = "";
      return;
    }

    const currentRange = getRange(value, this._rangesData);
    if (Number(this.baseElement.value) !== currentRange.id) {
      this.baseElement.value = currentRange.id - 1;
      this.baseElement.dispatchEvent(new Event("change"));
    }

    this._currentRange = currentRange;

    function getRange(index, ranges) {
      if (ranges.length <= 0) {
        throw new Error("The ranges array cannot be empty");
      }

      const loopedIndex = Math.abs(index) % ranges.length;
      return ranges[loopedIndex];
    }
  }
}

class SpecComponent extends AbstractComponent {
  constructor(specContainerElement, rangesData, defaultVariantId = null) {
    super(specContainerElement, rangesData, defaultVariantId);

    // Element dictionary
    this._elements = {
      hillClimbing: this.baseElement
        .querySelector("[js-comparison-spec='hillClimbing']"),
      maxRange: this.baseElement
        .querySelector("[js-comparison-spec='maxRange']"),
      measurementUnitSelector: document
        .querySelector("[js-comparison='measurementUnit']"),
      motorPower: this.baseElement
        .querySelector("[js-comparison-spec='motorPower']"),
      rideComfort: this.baseElement
        .querySelector("[js-comparison-spec='rideComfort']"),
      specList: this.baseElement
        .querySelector("[js-comparison-spec='list']"),
      stance: this.baseElement
        .querySelector("[js-comparison-spec='stance']"),
      topSpeed: this.baseElement
        .querySelector("[js-comparison-spec='topSpeed']"),
    }

    // Bindings
    this._handleUpdateMeasurementUnit = this._handleUpdateMeasurementUnit
      .bind(this);

    // Properties
    this._hillClimbing = Number(
      this._getDatasetValue(this._elements.hillClimbing, 3),
    );
    this._maxRange = Number(this._getDatasetValue(this._elements.maxRange, -1));
    this._measurementUnit = this._getDatasetValue(
      this._elements.measurementUnitSelector,
      "km",
    );
    this._motorPower = Number(
      this._getDatasetValue(this._elements.motorPower, -1),
    );
    this._rideComfort = Number(
      this._getDatasetValue(this._elements.rideComfort, 3),
    );
    this._stance = this._getDatasetValue(this._elements.stance, "");
    this._topSpeed = this._getDatasetValue(this._elements.topSpeed, -1);

    // Initialisation
    this.init();
  }

  _initListeners() {
    super._initListeners();
    this._elements.measurementUnitSelector.addEventListener(
      "change",
      this._handleUpdateMeasurementUnit,
    );
  }

  _handleUpdateMeasurementUnit(event) {
    const selectorEl = this._comparisonContainerElement
      .querySelector(`[js-comparison='selector'][data-id='${this.baseElement.dataset.id}']`);
    const variantId = Number(selectorEl.value);
    const product = findProductFromVariantId(this._rangesData, variantId);
    this._setMeasurementUnit(event.target.value);
    this._setMaxRange(product.maxRange);
    this._setTopSpeed(product.topSpeed);
  }

  _updateFromOption(_, product) {
    this._updateValuesFromProduct(product);
    this._updateBestSpecs();
  }

  _updateBestSpecs() {
    // const specContainers = this._comparisonContainerElement
  }

  _updateValuesFromProduct(product) {
    this._setHillClimbing(product.motorPower >= 900
      ? 4
      : 3
    );
    this._setMaxRange(product.maxRange);
    this._setMotorPower(product.motorPower);
    this._setRideComfort(product.rideComfort);
    this._setStance(product.stance);
    this._setTopSpeed(product.topSpeed);
  }

  _setHillClimbing(value) {
    if (!isValidNumber(value) || value > 4) {
      throw new Error("Invalid value passed to hillClimbing");
    }

    const classSuffix = "product-comparison__spec-bar";

    value = Math.abs(value);
    this._elements.hillClimbing.dataset.value = value.toString();
    this._elements.hillClimbing.classList.remove(
      `${classSuffix}--1`,
      `${classSuffix}--2`,
      `${classSuffix}--3`,
      `${classSuffix}--4`,
    );
    this._elements.hillClimbing.classList.add(
      `${classSuffix}--${value.toString()}`
    );

    this._hillClimbing = Number(value);
  }

  _setMaxRange(value) {
    if (!isValidNumber(value.metric)) {
      throw new Error("Invalid value passed to maxRange");
    }

    this._elements.maxRange.dataset.value = value.toString();
    this._elements.maxRange.innerHTML = value < 0
      ? "Unknown"
      : this._measurementUnit === "km"
        ? `${value.metric} km`
        : `${value.imperial ? value.imperial : convertKmToMi(value)} mi`

    this._maxRange = Number(value.metric);
  }

  _setMeasurementUnit(value) {
    if (!["km", "mi"].includes(value)) {
      throw new Error("Invalid value passed to measurementUnit");
    }

    this._measurementUnit = value;
  }

  _setMotorPower(value) {
    if (!isValidNumber(value)) {
      throw new Error("Invalid value passed to motorPower")
    }

    this._elements.motorPower.dataset.value = value.toString();
    this._elements.motorPower.innerHTML = value < 0
      ? "Unknown"
      : `${value} W`;

    this._motorPower = Number(value);
  }

  _setRideComfort(value) {
    if (!isValidNumber(value) || value > 4) {
      throw new Error("Invalid value passed to rideComfort");
    }

    const classSuffix = "product-comparison__spec-bar"

    this._elements.rideComfort.dataset.value = value.toString();
    this._elements.rideComfort.classList.remove(
      `${classSuffix}--1`,
      `${classSuffix}--2`,
      `${classSuffix}--3`,
      `${classSuffix}--4`,
    );
    this._elements.rideComfort.classList.add(
      `${classSuffix}--${(value < 0 ? 3 : value).toString()}`
    );

    this._rideComfort = Number(value);
  }

  _setStance(value) {
    value = value.trim();
    const infoClass = "product-comparison__spec--show-info";
    if (!["forward facing", "side facing"].includes(value.toLowerCase())) {
      this._elements.stance.dataset.value = "";
      this._elements.stance.innerHTML = "Unknown";
      this._elements.stance.classList.remove(infoClass);
      this._stance = "";
      return;
    }

    this._elements.stance.dataset.value = value;
    this._elements.stance.innerHTML = value;
    this._elements.stance.classList.toggle(
      infoClass,
      value.toLowerCase() === "forward facing",
    );

    this._stance = value;
  }

  _setTopSpeed(value) {
    if (!isValidNumber(value.metric)) {
      throw new Error("Invalid value passed to topSpeed");
    }

    if (value < 0) {
      this._elements.topSpeed.innerHTML = "Unknown";
      this._elements.topSpeed.dataset.value = "-1";
      this._topSpeed = -1;
    } else {
      this._elements.topSpeed.innerHTML = this._measurementUnit === "km"
        ? `${value.metric} km/h`
        : `${value.imperial ? value.imperial : convertKmToMi(value.metric)} mph`
      this._elements.topSpeed.dataset.value = value.metric.toString();
      this._topSpeed = value.metric;
    }
  }
}

function disableSiblingSelectors(selectorEls) {
  const selectedOptions = [];
  selectorEls.forEach((selectorEl) => {
    if (!isValidNumber(Number(selectorEl.value))) {
      return;
    }

    if (window.innerWidth > 1025 || Number(selectorEl.dataset.id) < 2) {
      const selectedOptionId = selectorEl.value;
      selectedOptions.push({
        id: Number(selectorEl.dataset.id),
        value: selectedOptionId,
      });

      const currentlyDisabled = selectorEl.querySelectorAll("option[disabled]");
      currentlyDisabled.forEach((o) => o.removeAttribute("disabled"));
    }
  });

  if (selectedOptions.length <= 0) {
    return;
  }

  selectedOptions.forEach(({ id, value }) => {
    const selectorsToDisableOn = Array
      .from(selectorEls)
      .filter((el) => Number(el.dataset.id) !== id);

    selectorsToDisableOn.forEach((el) => {
      const optToDisable = el.querySelector(`option[value="${value}"]`);

      if (optToDisable) {
        optToDisable.setAttribute("disabled", "disabled");
      }
    })
  })
}

function emphasiseBestSpecs(specEls, columnId, product) {
  const arrays = [];
  let specElsLength = window.innerWidth > 1025
    ? specEls.length
    : specEls.length - 1;

  for (let i = 0; i < specElsLength; i++) {
    const currSpecEl = specEls[i];
    const specItemEls = currSpecEl
      .querySelectorAll("[js-comparison-spec][data-value]");

    specItemEls.forEach((specItemEl) => {
      specItemEl.parentElement.classList
        .remove("product-comparison__spec-item--best");
    })

    const specItemElsArray = Array.from(specItemEls).map((specItemEl) => {
      const attrValue = specItemEl.getAttribute("js-comparison-spec");
      if (i === columnId) {
        return {
          id: currSpecEl.dataset.id,
          name: attrValue,
          value: attrValue === "hillClimbing"
            ? (product.motorPower > 900 ? 4 : product.motorPower > 700 ? 3 : 2)
            : product[attrValue],
        }
      }

      return {
        id: currSpecEl.dataset.id,
        name: specItemEl.getAttribute("js-comparison-spec"),
        value: Number(specItemEl.dataset.value),
      }
    });

    arrays.push(specItemElsArray);
  }

  const result = {};
  for (let i = 0; i < arrays[0].length; i++) {
    const name = arrays[0][i].name;
    let maxValue = -Infinity;
    let ids = [];
    for (let j = 0; j < arrays.length; j++) {
      const currObj = arrays[j][i];
      if (currObj.value > maxValue) {
        maxValue = currObj.value;
        ids = [Number(currObj.id)];
      } else if (currObj.value === maxValue) {
        ids.push(Number(currObj.id));
      }
    }

    result[name] = ids;
  }

  Object.entries(result).forEach(([key, value]) => {
    value.forEach((id) => {
      const elToHighlight = specEls[id]
        ?.querySelector(`[js-comparison-spec="${key}"]`);
      elToHighlight?.parentElement.classList
        .add("product-comparison__spec-item--best");
    });
  })
}

document.addEventListener(("DOMContentLoaded"), () => {
  const selectors = {
    bestFor: '[js-comparison="best"]',
    cardAtcForm: '[js-comparison-card="atcForm"]',
    container: '[js-comparison="container"]',
    feature: '[js-comparison="feature"]',
    header: '[js-comparison-header="container"]',
    info: '[js-comparison-tooltip="container"]',
    measurementUnit: '[js-comparison="measurementUnit"]',
    productCard: '[js-comparison="card"]',
    productsData: '[js-comparison="dataTable"]',
    selector: '[js-comparison="selector"]',
    selectorAtcForm: '[js-comparison-selector="atcForm"]',
    selectorContainer: '[js-comparison="selectorContainer"]',
    spec: '[js-comparison="spec"]',
  }

  const elements = {
    bestForElements: document.querySelectorAll(selectors.bestFor),
    cardAtcForm: document.querySelector(selectors.cardAtcForm),
    container: document.querySelector(selectors.container),
    featureElements: document.querySelectorAll(selectors.feature),
    headerContainerElement: document.querySelector(selectors.header),
    infoContainerElements: document.querySelectorAll(selectors.info),
    measurementUnitSelector: document.querySelector(selectors.measurementUnit),
    productCardElements: document.querySelectorAll(selectors.productCard),
    productsDataScriptElement: document.querySelector(selectors.productsData),
    selectorContainerElement: document.querySelector(
      selectors.selectorContainer),
    selectorElements: document.querySelectorAll(selectors.selector),
    selectorAtcForms: document.querySelectorAll(selectors.selectorAtcForm),
    specElements: document.querySelectorAll(selectors.spec),
  }

  const queryParamValue = retrieveQueryParam();

  // Initialise products data
  const productsData = parseLookupTableElement(
    elements.productsDataScriptElement);
  productsData.ranges = formatRangesData(productsData?.ranges || []);

  // Setup header data
  if (elements.headerContainerElement) {
    new Header(
      elements.headerContainerElement,
      productsData.ranges,
      queryParamValue || null,
    );
  }

  // Setup product cards
  elements.productCardElements.forEach((productCardEl, index) => {
    new CardComponent(
      productCardEl,
      productsData.ranges,
      index === 0 ? queryParamValue : null,
    );
  });

  elements.container.addEventListener("option:change", (event) => {
    disableSiblingSelectors(elements.selectorElements);
    emphasiseBestSpecs(
      elements.specElements,
      event.detail.id,
      event.detail.product,
    );
  });

  // Setup info elements
  elements.infoContainerElements.forEach((infoContainerEl, index) => {
    new InfoComponent(
      infoContainerEl,
      productsData.ranges,
      index === 0 ? queryParamValue : null,
    );
  })

  // Setup spec elements
  elements.specElements.forEach((specEl, index) => {
    new SpecComponent(
      specEl,
      productsData.ranges,
      index === 0 ? queryParamValue : null,
    );
  });

  // Setup best for elements
  elements.bestForElements.forEach((bestEl, index) => {
    new BestForComponent(
      bestEl,
      productsData.ranges,
      index === 0 ? queryParamValue : null,
    );
  });

  // Setup feature elements
  elements.featureElements.forEach((featureEl, index) => {
    new FeatureComponent(
      featureEl,
      productsData.ranges,
      index === 0 ? queryParamValue : null,
    );
  });

  // Setup selector elements
  elements.selectorElements.forEach((selectorEl, index) => {
    new SelectorComponent(
      selectorEl,
      productsData.ranges,
      queryParamValue,
    );
  });

  if (elements.measurementUnitSelector) {
    elements.measurementUnitSelector.addEventListener("change", (event) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'selectChangeMeasurement',
        selectName: event.currentTarget.getAttribute("js-comparison"),
        selectValue: event.currentTarget.value,
      });
    });
  }

  window.addEventListener("load", () => {
    const headerHeight = window.innerWidth > 1025 ? 70 : 60;
    elements.selectorContainerElement.classList.toggle(
      "product-comparison__selectors--sticky",
      (
        elements.selectorContainerElement.getBoundingClientRect().top
        - headerHeight
      ) <= 0,
    );
  })

  window.addEventListener("resize", () => {
    const headerHeight = window.innerWidth > 1025 ? 70 : 60;
    elements.selectorContainerElement.classList.toggle(
      "product-comparison__selectors--sticky",
      (
        elements.selectorContainerElement.getBoundingClientRect().top
        - headerHeight
      ) <= 0,
    );
  })

  window.addEventListener(
    "scroll",
    () => {
      const headerHeight = window.innerWidth > 1025 ? 71 : 61;
      elements.selectorContainerElement.classList.toggle(
        "product-comparison__selectors--sticky",
        (
          elements.selectorContainerElement.getBoundingClientRect().top
          - headerHeight
        ) <= 0,
      );

      elements.selectorAtcForms.forEach((atcForm) => atcForm.classList.toggle(
        "product-comparison__selector-form--show",
        elements.cardAtcForm.getBoundingClientRect().top < 0,
      ));
    },
    { passive: true }
  )
});